import React, { useEffect } from 'react';
import { notificationService } from '@/lib/api/notification-service';
import { useAuth } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/toaster';
import { toast } from '@/hooks/use-toast';

type ToastPosition = NonNullable<React.ComponentProps<typeof Toaster>['position']>;

const DEFAULT_TOAST_POSITION: ToastPosition = 'top-right';

function parseToastPosition(value: string): ToastPosition {
  switch (value) {
    case 'top-left':
    case 'top-center':
    case 'top-right':
    case 'bottom-left':
    case 'bottom-center':
    case 'bottom-right':
      return value;
    default:
      throw new Error(`Unsupported toast position from notification settings: ${value}`);
  }
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [toastPosition, setToastPosition] = React.useState<ToastPosition>(DEFAULT_TOAST_POSITION);

  // Load toast settings from backend only if authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setToastPosition(DEFAULT_TOAST_POSITION);
      return;
    }

    let isCurrent = true;

    const loadSettings = async (): Promise<void> => {
      try {
        const settings = await notificationService.getToastSettings();
        const position = parseToastPosition(settings.toast_position);
        if (isCurrent) setToastPosition(position);
      } catch (error: unknown) {
        console.error('Failed to load toast settings:', error);
        if (isCurrent) {
          toast({
            title: 'Benachrichtigungseinstellungen nicht geladen',
            description: 'Die Darstellung der Hinweise konnte nicht konfiguriert werden.',
            variant: 'destructive',
          });
        }
      }
    };

    void loadSettings();

    return () => {
      isCurrent = false;
    };
  }, [isAuthenticated]);

  return (
    <>
      {children}
      {/* Non-error toasts: Admin-defined position */}
      <Toaster position={toastPosition} filterVariant="non-destructive" />
      {/* Error toasts: always bottom-right */}
      <Toaster position="bottom-right" filterVariant="destructive" />
    </>
  );
};
